import { Injectable } from '@angular/core';
import { IdeaEvidence } from '@models/Sessions/Idea/evidence';
import { Lang } from '@models/Sessions/Idea/language';
import {
  AddUniqueFactsResult,
  BrainstormingBody,
  BrainstormingResult,
  CheckInconsistenciesResult,
  CheckProblemsResult,
  FixIssuesResult,
  GenerateBulletpointsResult,
  GetArticleInputTopicResult,
  GetArticleInputUrlOrTextBody,
  GetArticleInputUrlOrTextResult,
  GetFeedbackResult,
  GetOutlineInputTextBody,
  GetOutlineInputTextResult,
  GetOutlineInputTopicBody,
  GetOutlineInputTopicResult,
  GetScriptInputTextBody,
  GetScriptInputTextResult,
  GetScriptInputTopicBody,
  GetScriptInputTopicResult,
  ImproveScriptResult,
  ImproveScriptWithCustomInstructionResult,
  ImproveTextBasedOnUserCustomInstructionAndExtraContentResult,
  IntegrateFactsResult,
  IssueItem,
  PhoneticSpellingPolishResult,
  ScriptEvidence,
  SearchMissingFactsResult,
  SearchUniqueFactsResult,
  UltimateGenerateRequest,
  UltimateGenerateResponse,
} from '@models/Sessions/Idea/podcast-api';
import { fileUrlFromBlob } from '@utils/file';
import { convertToMarkdown } from '@utils/text-content';
import { map, mergeMap, Observable, of } from 'rxjs';

import { PodcastApiHttpClient } from './podcast-api.http-client';

@Injectable()
export class PodcastService {
  constructor(private httpClient: PodcastApiHttpClient) {}

  ultimateGenerate(params: UltimateGenerateRequest) {
    const endpoint = `ultimate_generate`;
    return this.httpClient.post<UltimateGenerateResponse>(endpoint, params);
  }

  brainstormConcepts(params: BrainstormingBody) {
    return this.httpClient
      .post<BrainstormingResult>('brainstorming', {
        ...params,
        context: params.context ?? '',
        alreadyGeneratedConcepts: params.alreadyGeneratedConcepts ?? [],
      })
      .pipe(map(res => res.concepts));
  }

  generateOutlineInputTopic(
    params: GetOutlineInputTopicBody,
    shouldIncludeResearch = true,
  ) {
    /*
      Example of the request body
      {
        "topic": "string",
        "context": "",
        "concept": "",
        "user_text": "string",
        "num_questions": 3,
        "num_search_queries": 2,
        "num_search_websites": 5,
        "search_type": "search",
        "search_tbs": "qdr:h",
        "custom_urls_list": [
          "string"
        ],
        "prompt_language": "en",
        "llm": "gpt-4o"
      }
    */

    const endpoint = `gen_outline_input_topic_${shouldIncludeResearch ? 'with_' : 'without_'}research`;
    return this.httpClient.post<GetOutlineInputTopicResult>(endpoint, {
      ...params,
      context: params.context ?? '',
      concept: params.concept?.join(';') ?? [],
    });
  }

  generateScriptInputTopic(
    params: GetOutlineInputTopicBody & GetScriptInputTopicBody,
    shouldIncludeResearch = true,
  ): Observable<
    Pick<GetScriptInputTopicResult, 'script'> &
      Partial<Pick<GetScriptInputTopicResult, 'evidence'>> &
      Partial<Pick<GetOutlineInputTopicResult, 'outline'>> & {
        shortTitle?: string;
        oneLiner?: string;
      }
  > {
    if (!params.outline) {
      return this.generateOutlineInputTopic(params, shouldIncludeResearch).pipe(
        mergeMap(({ outline, short_title, one_liner }) =>
          this.generateScriptInputTopic(
            {
              ...params,
              outline,
            },
            shouldIncludeResearch,
          ).pipe(
            map(res => ({
              ...res,
              outline,
              shortTitle: short_title,
              oneLiner: one_liner,
            })),
          ),
        ),
      );
    }

    const endpoint = `gen_${params.numberOfGuests > 0 ? 'discussion_' : ''}script_input_topic_${shouldIncludeResearch ? 'with_' : 'without_'}research`;

    return this.httpClient.post<GetScriptInputTopicResult>(endpoint, {
      ...params,
      context: params.context ?? '',
    });
  }

  generateOutlineInputText(params: GetOutlineInputTextBody) {
    return this.httpClient.post<GetOutlineInputTextResult>(
      'gen_outline_input_text',
      {
        ...params,
        context: params.context ?? '',
      },
    );
  }

  generateScriptInputText(
    params: GetOutlineInputTextBody & GetScriptInputTextBody,
    referenceText?: string,
  ): Observable<
    GetScriptInputTextResult &
      Partial<Pick<GetOutlineInputTextResult, 'outline'>> & {
        shortTitle?: string;
        oneLiner?: string;
      }
  > {
    if (referenceText) {
      return this.httpClient
        .post<GetArticleInputTopicResult>(
          `gen_article_input_text_and_ref_without_research`,
          {
            ...params,
            userText: referenceText,
            context: params.context ?? '',
          },
        )
        .pipe(
          map(({ article, short_title, one_liner }) => ({
            script: article,
            shortTitle: short_title,
            oneLiner: one_liner,
          })),
        );
    }
    if (!params.outline) {
      return this.generateOutlineInputText(params).pipe(
        mergeMap(({ outline, short_title, one_liner }) =>
          this.generateScriptInputText({
            ...params,
            outline,
          }).pipe(
            map(res => ({
              ...res,
              outline,
              shortTitle: short_title,
              oneLiner: one_liner,
            })),
          ),
        ),
      );
    }

    const endpoint = `gen_${params.numberOfGuests > 0 ? 'discussion_' : ''}script_input_text_without_research`;

    return this.httpClient.post<GetScriptInputTextResult>(endpoint, {
      ...params,
      context: params.context ?? '',
    });
  }

  generateArticleInputUrlOrText(
    params: Omit<GetArticleInputUrlOrTextBody, 'context'> &
      Partial<Pick<GetArticleInputUrlOrTextBody, 'context'>>,
  ): Observable<{
    script: string;
    shortTitle?: string;
    oneLiner?: string;
    inconsistency?: string;
  }> {
    const endpoint = `gen_article_input_url_or_text_and_ref`;

    return this.httpClient
      .post<GetArticleInputUrlOrTextResult>(endpoint, {
        ...params,
        context: params.context ?? '',
      })
      .pipe(
        map(res => ({
          script: res.article,
          evidence: res.evidence,
          shortTitle: res.short_title,
          oneLiner: res.one_liner,
          inconsistency: res.inconsistency,
        })),
      );
  }

  improveScript(
    script: string,
    feedback?: GetFeedbackResult | null,
    promptLanguage?: string,
  ) {
    return (
      feedback
        ? of(feedback)
        : this.httpClient.post<GetFeedbackResult>('gen_feedback', {
            podcastScriptResponseRaw: convertToMarkdown(script),
            promptLanguage,
          })
    ).pipe(
      mergeMap(fedback =>
        this.httpClient.post<ImproveScriptResult>('improve_script', {
          ...fedback,
          podcastScriptRaw: convertToMarkdown(script),
          promptLanguage,
        }),
      ),
    );
  }

  addUniqueFacts(
    script: string,
    evidence: IdeaEvidence[],
    promptLanguage?: string,
  ) {
    return this.httpClient.post<AddUniqueFactsResult>(
      'add_unique_facts_in_one_step',
      {
        existingPodcastScript: convertToMarkdown(script),
        existingEvidence: evidence,
        promptLanguage,
      },
    );
  }

  searchUniqueFacts(
    script: string,
    evidence: IdeaEvidence[],
    promptLanguage?: string,
  ) {
    return this.httpClient.post<SearchUniqueFactsResult>(
      'search_unique_facts',
      {
        existingPodcastScript: convertToMarkdown(script),
        existingEvidence: evidence,
        promptLanguage,
      },
    );
  }

  searchMissingFacts(
    outputText: string,
    params: {
      inputUrlOrTextList: string[];
      promptLanguage?: string;
    },
  ) {
    return this.httpClient.post<SearchMissingFactsResult>(
      'find_missing_facts',
      {
        inputUrlOrTextList: params.inputUrlOrTextList.filter(Boolean),
        promptLanguage: params.promptLanguage,
        outputText: convertToMarkdown(outputText),
      },
    );
  }

  integrateFacts(
    script: string,
    evidence: IdeaEvidence[],
    evidenceToIntegrate: ScriptEvidence[],
    promptLanguage?: string,
  ) {
    return this.httpClient.post<IntegrateFactsResult>(
      'integrate_facts_with_reasoning',
      {
        existingPodcastScript: convertToMarkdown(script),
        existingEvidence: evidence,
        evidenceToIntegrate,
        promptLanguage,
      },
    );
  }

  addMissingFactsWithReasoning(
    script: string,
    missingFactsDescription: string,
    missingFactsList: IssueItem[],
    promptLanguage: string,
  ) {
    return this.httpClient.post<FixIssuesResult>(
      'add_missing_facts_with_reasoning',
      {
        llm_text: convertToMarkdown(script),
        missingFactsDescription,
        missingFactsList,
        promptLanguage,
      },
    );
  }

  solveProblemsWithReasoning(
    script: string,
    problemsDescription: string,
    problemsList: IssueItem[],
    promptLanguage: string,
  ) {
    return this.httpClient.post<FixIssuesResult>(
      'solve_problems_with_reasoning',
      {
        llm_text: convertToMarkdown(script),
        problemsDescription,
        problemsList,
        promptLanguage,
      },
    );
  }

  generateBulletpoints(script: string, promptLanguage?: string) {
    return this.httpClient.post<GenerateBulletpointsResult>(
      'generate_bulletpoints_based_on_user_custom_instruction',
      {
        podcastScriptRaw: convertToMarkdown(script),
        promptLanguage,
      },
    );
  }

  improveScriptWithCustomInstruction(
    script: string,
    userInstruction: string,
    promptLanguage?: string,
  ) {
    return this.httpClient.post<ImproveScriptWithCustomInstructionResult>(
      'improve_script_based_on_user_custom_instruction',
      {
        podcastScriptRaw: convertToMarkdown(script),
        userInstruction,
        promptLanguage,
      },
    );
  }

  improveTextBasedOnUserCustomInstructionAndExtraContent(
    text: string,
    userInstruction: string,
    params: {
      inputUrlOrTextList: string[];
      promptLanguage?: string;
    },
  ) {
    return this.httpClient
      .post<ImproveTextBasedOnUserCustomInstructionAndExtraContentResult>(
        'improve_text_based_on_user_custom_instruction_and_extra_content',
        {
          text: convertToMarkdown(text),
          userInstruction,
          inputUrlOrTextList: params.inputUrlOrTextList.filter(Boolean),
          promptLanguage: params.promptLanguage,
        },
      )
      .pipe(
        map(({ article, explanation }) => ({ script: article, explanation })),
      );
  }

  expandText(
    markedText: string,
    originalText: string,
    evidenceList: IdeaEvidence[],
    promptLanguage: string,
    userInstruction?: string,
  ) {
    originalText = convertToMarkdown(originalText);
    return this.httpClient
      .post<{ article: string; explanation: string }>(
        'expand_text_based_on_the_next_citation_source',
        {
          markedText,
          originalText,
          evidenceList,
          userInstruction,
          promptLanguage,
        },
      )
      .pipe(
        map(res => {
          if (res.article === originalText) {
            res.article = null;
          }
          return res;
        }),
      );
  }

  phoneticSpellingPolish(script: string, promptLanguage?: string) {
    return this.httpClient.post<PhoneticSpellingPolishResult>(
      'phonetic_spelling_polish',
      {
        polishPodcastScriptResponseRaw: convertToMarkdown(script),
        promptLanguage,
      },
    );
  }

  textToSpeech(inputText: string) {
    return this.httpClient
      .post(
        'text_to_speech',
        {
          inputText: convertToMarkdown(inputText),
          selectedVoice: 'nova',
        },
        {
          observe: 'response',
          responseType: 'blob',
        },
      )
      .pipe(mergeMap(({ body }) => fileUrlFromBlob(body)));
  }

  translate(text: string, targetLang: Lang) {
    return this.httpClient
      .post<{ script: string }>('translation', {
        text: convertToMarkdown(text),
        targetLang,
      })
      .pipe(map(({ script }) => script));
  }

  checkInconsistencies(outputText: string, promptLanguage: string) {
    return this.httpClient.post<CheckInconsistenciesResult>(
      'check_inconsistencies',
      {
        outputText,
        promptLanguage,
      },
    );
  }

  checkProblemsAndInconsistencies(
    inputUrlOrTextList: string[],
    outputText: string,
    promptLanguage: string,
  ) {
    return this.httpClient.post<
      CheckProblemsResult & CheckInconsistenciesResult
    >('check_problems_and_inconsistencies', {
      inputUrlOrTextList,
      outputText,
      promptLanguage,
    });
  }
}
